import React, { useState, useMemo, useEffect } from 'react';
import MainLayout from '../../../shared/layouts/MainLayout';
import { Card, Alert, Loading, Badge, Spinner } from '../../../shared/components';
import { useReports } from '../hooks/useReports';
import { useExport } from '../hooks/useExport';
import ReportCharts from '../components/ReportCharts';
import ExportButtons from '../components/ExportButtons';
import ReportTable from '../components/ReportTable';
import ReportFilters from '../components/ReportFilters';

// Movement type icons and colors
const movementConfig = {
  'PURCHASE': { 
    color: 'text-green-600', 
    bgColor: 'bg-green-100', 
    icon: '📦',
    description: 'Stock received from suppliers'
  },
  'ISSUE': { 
    color: 'text-blue-600', 
    bgColor: 'bg-blue-100', 
    icon: '📤',
    description: 'Items issued to patients'
  },
  'RETURN': { 
    color: 'text-purple-600', 
    bgColor: 'bg-purple-100', 
    icon: '↩️',
    description: 'Items returned to stock'
  },
  'DISPOSAL': { 
    color: 'text-red-600', 
    bgColor: 'bg-red-100', 
    icon: '🗑️',
    description: 'Expired or damaged items'
  },
  'ADJUSTMENT': { 
    color: 'text-orange-600', 
    bgColor: 'bg-orange-100', 
    icon: '⚖️',
    description: 'Inventory count adjustments'
  }
};

const MovementSummaryPage = () => {
  const [filters, setFilters] = useState({
    period: 'monthly',
    startDate: '',
    endDate: '',
    movementType: 'all'
  });
  
  const { data, loading, error, refetch } = useReports('movement', filters);
  const exportUtils = useExport();

  // Debug: Log the actual data structure
  useEffect(() => {
    console.log('MovementSummaryPage - Full data from API:', data);
    if (data) {
      console.log('MovementSummaryPage - data.data:', data.data);
      console.log('MovementSummaryPage - data.data.data:', data.data?.data);
      console.log('MovementSummaryPage - Is data.data.data an array?', Array.isArray(data.data?.data));
    }
  }, [data]);

  // Extract the actual data array from the nested structure
  const getRawDataArray = () => {
    if (!data) return [];
    
    // Based on your API response structure
    if (data.data && data.data.data && Array.isArray(data.data.data)) {
      return data.data.data;
    }
    
    // Fallback: try data.data if it's an array
    if (data.data && Array.isArray(data.data)) {
      return data.data;
    }
    
    // Fallback: try data itself if it's an array
    if (Array.isArray(data)) {
      return data;
    }
    
    return [];
  };

  const rawData = getRawDataArray();

  // Apply local filters
  const filteredData = useMemo(() => {
    if (!rawData.length) return [];
    
    return rawData.filter(item => {
      if (filters.movementType !== 'all' && item.movement_type !== filters.movementType) {
        return false;
      }
      return true;
    });
  }, [rawData, filters.movementType]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalTransactions = filteredData.reduce((sum, item) => 
      sum + parseInt(item.total_transactions), 0
    );
    const totalQuantity = filteredData.reduce((sum, item) => 
      sum + parseInt(item.total_quantity), 0
    );
    
    // Find most active movement type
    let mostActive = { type: '', transactions: 0 };
    filteredData.forEach(item => {
      if (parseInt(item.total_transactions) > mostActive.transactions) {
        mostActive = { 
          type: item.movement_type, 
          transactions: parseInt(item.total_transactions) 
        };
      }
    });
    
    return { totalTransactions, totalQuantity, mostActive };
  }, [filteredData]);

  const columns = [
    { 
      header: 'Movement Type', 
      accessor: 'movement_type',
      render: (value) => {
        const config = movementConfig[value] || { color: 'text-gray-600', bgColor: 'bg-gray-100', icon: '📊' };
        return (
          <div className="flex items-center gap-2">
            <span className={`${config.bgColor} px-2 py-1 rounded text-sm`}>
              {config.icon}
            </span>
            <span className={config.color}>{value}</span>
          </div>
        );
      }
    },
    { 
      header: 'Total Transactions', 
      accessor: 'total_transactions',
      render: (value) => (
        <span className="font-semibold">{parseInt(value).toLocaleString()}</span>
      )
    },
    { 
      header: 'Total Quantity', 
      accessor: 'total_quantity',
      render: (value) => (
        <span className="font-semibold">{parseInt(value).toLocaleString()} units</span>
      )
    },
    {
      header: 'Avg per Transaction',
      accessor: 'avg_per_transaction',
      render: (value, row) => {
        const avg = Math.round(parseInt(row.total_quantity) / parseInt(row.total_transactions));
        return <span>{avg} units</span>;
      }
    }
  ];

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    if (newFilters.startDate || newFilters.endDate || newFilters.period !== filters.period) {
      refetch();
    }
  };

  const handleReset = () => {
    setFilters({
      period: 'monthly',
      startDate: '',
      endDate: '',
      movementType: 'all'
    });
    refetch();
  };

  // Handler functions for exports
  const handleCSVExport = () => {
    exportUtils.exportToCSV(filteredData, 'movement_summary');
  };

  const handleExcelExport = () => {
    exportUtils.exportToExcel(filteredData, 'movement_summary');
  };

  const handlePDFExport = () => {
    exportUtils.exportToPDF(
      filteredData, 
      columns, 
      'Movement Summary Report', 
      'movement_summary'
    );
  };

  const handlePrint = () => {
    exportUtils.print();
  };

  if (loading) return <Spinner />;
  if (error) return <Alert type="error" message={error} />;

  return (
    // <MainLayout>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Movement Summary Report</h1>
        
        {/* Filters */}
        <ReportFilters
          filters={filters}
          onApply={handleApplyFilters}
          onReset={handleReset}
        />
        
        {/* Statistics Cards */}
        {filteredData.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-500">Total Transactions</h3>
                <p className="text-2xl font-bold text-blue-600">
                  {stats.totalTransactions.toLocaleString()}
                </p>
              </div>
            </Card>
            <Card>
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-500">Total Quantity Moved</h3>
                <p className="text-2xl font-bold text-green-600">
                  {stats.totalQuantity.toLocaleString()} units
                </p>
              </div>
            </Card>
            <Card>
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-500">Most Active</h3>
                <p className="text-xl font-bold text-purple-600">
                  {stats.mostActive.type}
                </p>
                <p className="text-xs text-gray-500">
                  {stats.mostActive.transactions} transactions
                </p>
              </div>
            </Card>
          </div>
        )}
        
        {/* Export Buttons */}
        <ExportButtons
          onExportCSV={handleCSVExport}
          onExportExcel={handleExcelExport}
          onExportPDF={handlePDFExport}
          onPrint={handlePrint}
          disabled={loading}
          showRecordCount={true}
          recordCount={filteredData.length}
        />
        
        {/* Main Content - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Table Card */}
          <Card>
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Movement Summary Table</h2>
                {filters.movementType !== 'all' && (
                  <Badge variant="info">{filters.movementType}</Badge>
                )}
              </div>
              
              {filteredData.length > 0 ? (
                <ReportTable
                  data={{ data: filteredData }}
                  columns={columns}
                />
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No movement data available</p>
                </div>
              )}
            </div>
          </Card>
          
          {/* Charts Card */}
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-4">Movement Distribution</h2>
              {filteredData.length > 0 ? (
                <>
                  <ReportCharts
                    data={filteredData}
                    type="movement"
                    title="Movement Analysis"
                  />
                  
                  {/* Movement Type Legends */}
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {filteredData.map((item, index) => {
                      const config = movementConfig[item.movement_type] || 
                                    { color: 'text-gray-600', bgColor: 'bg-gray-100', icon: '📊' };
                      return (
                        <div key={index} className="flex items-center gap-2 text-sm">
                          <span className={`${config.bgColor} px-2 py-1 rounded`}>
                            {config.icon}
                          </span>
                          <span className={config.color}>{item.movement_type}</span>
                          <span className="text-gray-500 ml-auto">
                            {item.total_transactions} txns
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No chart data available</p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Movement Type Descriptions */}
        {filteredData.length > 0 && (
          <Card className="mt-6">
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Movement Type Descriptions</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {Object.entries(movementConfig).map(([type, config]) => {
                  const movement = filteredData.find(m => m.movement_type === type);
                  return (
                    <div key={type} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`${config.bgColor} px-2 py-1 rounded`}>
                          {config.icon}
                        </span>
                        <span className={`font-medium ${config.color}`}>{type}</span>
                      </div>
                      <p className="text-xs text-gray-600 mb-2">{config.description}</p>
                      {movement && (
                        <div className="text-xs">
                          <span className="text-gray-500">
                            {movement.total_transactions} transactions,{' '}
                            {movement.total_quantity} units
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        )}
      </div>
    // </MainLayout>
  );
};

export default MovementSummaryPage;